// Async play — a game that waits for you.
//
// Matt, 2026-10-04: "implement async games for all of the board games on
// around table round. So all turn based games except commonwealth." Every
// game's online room already has meta/state/presence and a seq token in the
// one shared Firebase project, so async is a FLAG ON THE ROOM, `meta.async`,
// and this module is what the flag means:
//
//  - Nobody is bot-played for dropping out. (In a live room a bot takes a
//    human's seat after a grace period; in an async room the game waits.)
//  - The room is not swept after a day: in progress it lives 60 days, a
//    finished one 7.
//  - After every move the committing client writes WHO THE GAME IS WAITING
//    ON (`meta.waitingOn`, uids → true) and when (`meta.turnAt`), from this
//    game's adapter. The hub's atr-push Lambda reads that from every game,
//    keeps each player's "Your games" list at `atrAsync/<uid>` and pushes
//    "Your turn" — so no game needs its own push stack.
//
// HOUSE MODULE: the core of this file is byte-identical in every Around
// Table Round game (the same discipline as userStats.js and the rules file).
// Change it in one repo and copy it to the rest in the same session. The
// per-game part is the ADAPTER passed in: `waitingOnOf(gameState)` → uids
// the game is waiting on (the active seat for a turn-based game; everyone
// who has not submitted for a simultaneous one), and `isOver(gameState)`.

// Keyed by every status the games use: the No Thanks lineage says
// in-progress/finished, Telestrations' says playing/reveal, Cartographers'
// playing/over, Codenames' playing/finished.
const WEEK = 7 * 24 * 60 * 60 * 1000;
export const ASYNC_DEAD_AFTER_MS = {
  lobby: WEEK,                  // "I'll ask people this weekend"
  'in-progress': 60 * 24 * 60 * 60 * 1000,
  playing: 60 * 24 * 60 * 60 * 1000,
  reveal: 60 * 24 * 60 * 60 * 1000,
  finished: WEEK,
  over: WEEK,
};

export const isAsyncRoom = (meta) => meta?.async === true;

/** The sweep limit for a room: async rooms get the long ones. */
export const deadAfterFor = (meta, liveLimits) => (isAsyncRoom(meta) ? ASYNC_DEAD_AFTER_MS : liveLimits)[meta?.status]
  ?? (isAsyncRoom(meta) ? ASYNC_DEAD_AFTER_MS : liveLimits).lobby;

/**
 * The `meta` fields to write after a move (or on start): who the game waits
 * on now, and when that became true. `turnAt` only moves when the set of
 * people waited on CHANGES, so a push is news — "it's your turn" once, not
 * once per two-minute sweep. Pure.
 */
export function waitingPatch (gameState, previousMeta, adapter, now = Date.now()) {
  const over = Boolean(adapter.isOver?.(gameState));
  const uids = over ? [] : (adapter.waitingOnOf(gameState) || []).filter(Boolean);
  const waitingOn = Object.fromEntries(uids.map((uid) => [uid, true]));
  const before = Object.keys(previousMeta?.waitingOn || {}).sort().join(',');
  const after = uids.slice().sort().join(',');
  return {
    waitingOn: uids.length ? waitingOn : null,
    turnAt: before === after && previousMeta?.turnAt ? previousMeta.turnAt : now,
    lastActiveAt: now,
    ...(over ? { status: 'finished' } : {}),
  };
}

/**
 * The same patch for games whose "who is waiting" is computed by the store
 * from several nodes (Telestrations' pages, Cartographers' placements)
 * rather than from one state object. Pure.
 */
export const waitingPatchFromUids = (uids, previousMeta, over = false, now = Date.now()) =>
  waitingPatch(null, previousMeta, { waitingOnOf: () => uids, isOver: () => over }, now);

/** Am I among the people this room is waiting on? */
export const waitingOnMe = (meta, uid) => Boolean(uid && meta?.waitingOn?.[uid]);

/** Short "Waiting on Carrie and Natalie" / "Your turn" for a banner. Pure. */
export function waitingLine (meta, uid, nameOf = (id) => id) {
  const uids = Object.keys(meta?.waitingOn || {});
  if (!uids.length) return '';
  if (uids.includes(uid)) return uids.length === 1 ? 'Your turn.' : `Your turn — and ${uids.filter((u) => u !== uid).map(nameOf).join(', ')}.`;
  const names = uids.map(nameOf);
  return `Waiting on ${names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`}.`;
}

/** The hub's index row for this player, as the Lambda writes it. Shape only. */
export const ASYNC_INDEX = 'atrAsync';
export const ASYNC_PUSH = 'atrPush';
